/**
 * The tour's start (owner, 2026-10-04, option A on the canvas "Tour start: the reading wait").
 * The moment Take a 2-minute tour is clicked, this window says the tour is being set up, while
 * the real screen reads the five samples behind it ("a great way to show off our wizard").
 * It used to take five seconds for anything to happen, then an empty page, then a half-filled
 * list still saying "Reading…". Now there is one waiting screen with real progress only: the
 * engine's own step and the documents read, never an estimate. When all five are read it says
 * so, with the time it took on this computer, and a moment later step 1 opens by itself: there is
 * no Start button, because the tour has already started (owner, 2026-10-04: hence Leave tour).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { hostHas } from '../shared/host-capabilities';
import { Check, LoaderCircle, Play } from 'lucide-react';
import {
  TOUR_DOCUMENTS,
  tourReadCount,
  tourReadPeak,
  tourReadingStep,
  tourReadyLine,
  writeTour,
} from './tour';
import { readerProblemStep, type ReaderProblem } from './not-read';
import { TechnicalDetails } from './TechnicalDetails';
import { TOUR_LEAVE_EVENT, useTour } from './TourCard';
import { useJobProcessingState } from './JobProcessingControl';
import { TourStartFailed, useTourReadingFailed } from '../paige/TourStartFailed';
import { retryTourFeed, useTourFeedFailed } from '../paige/tour-feed'; // PAIGE
import { tourTelemetry } from './tour-telemetry';
import './tour.css';

/**
 * What the tour job's workspace is reading: the engine's current stage ("recognizing"; the window
 * names it in words, without the workspace's page count), each document's type once read (null
 * while it is still being read, or being read again after a first reading that didn't finish), how
 * many are being read again, whether the field reader itself can't read (`problem`: then nothing is
 * promised, not-read.ts), and whether anything is still reading or queued.
 */
export type TourReading = {
  jobId: string;
  stage: string | null;
  types: (string | null)[];
  again: number;
  problem?: { cause: ReaderProblem; detail: string } | null;
  reading: boolean;
};

const READING_EVENT = 'capturepoint-tour-reading';
let latest: TourReading | null = null;
let clickedAt: number | null = null;

/** The click on Take a 2-minute tour: the time the window reports is measured from here. */
export function noteTourClicked(): void {
  clickedAt = Date.now();
}

function publish(next: TourReading): void {
  if (latest && JSON.stringify(latest) === JSON.stringify(next)) return;
  latest = next;
  window.dispatchEvent(new Event(READING_EVENT));
}

/** Rendered by the tour job's workspace while the five are read; it tells the window what it shows. */
export function TourReadingReport(props: TourReading) {
  const { jobId, stage, again, reading } = props;
  const problem = JSON.stringify(props.problem ?? null);
  const types = JSON.stringify(props.types);
  useEffect(() => {
    publish({
      jobId,
      stage,
      again,
      problem: JSON.parse(problem),
      reading,
      types: JSON.parse(types),
    });
  }, [jobId, stage, again, problem, reading, types]);
  return null;
}

function useTourReading(): TourReading | null {
  const [reading, setReading] = useState(latest);
  useEffect(() => {
    const update = () => setReading(latest);
    update();
    window.addEventListener(READING_EVENT, update);
    return () => window.removeEventListener(READING_EVENT, update);
  }, []);
  return reading;
}

/** A page, drawn small: read, being read, or not yet read. */
function Tile({ state, label }: { state: 'read' | 'reading' | 'waiting'; label?: string | null }) {
  return (
    <li className={`tour-start-tile ${state}`}>
      <span className="tour-start-page" aria-hidden="true">
        {state !== 'waiting' && (
          <>
            <i />
            <i />
            <i />
            <b />
            <i />
            <i />
            <i />
          </>
        )}
      </span>
      <small>
        {state === 'read' ? (
          <>
            <Check size={13} strokeWidth={3} aria-hidden="true" />
            {label}
          </>
        ) : state === 'reading' ? (
          <>
            <LoaderCircle className="spin" size={12} aria-hidden="true" />
            Reading…
          </>
        ) : null}
      </small>
    </li>
  );
}

/**
 * The window. Shown from the click (`starting`, before the tour's job exists) until step 1; it
 * waits for every document to be read and the job to stop processing, for a moment (reading pauses
 * between passes), then says the tour is ready and, after READY_SHOWN, lets step 1 open.
 */
const READY_SHOWN = 2500;
export function TourStart({ starting }: { starting: boolean }) {
  const tour = useTour();
  const reading = useTourReading();
  const readingFailed = useTourReadingFailed(tour?.jobId); // PAIGE
  const feedFailed = useTourFeedFailed(tour?.jobId); // PAIGE
  const waiting = Boolean(tour && !tour.own && !tour.ready);
  const jobId = waiting ? tour!.jobId : '';
  const processing = useJobProcessingState(jobId);
  const busy = processing
    ? processing.status === 'running' ||
      processing.status === 'queued' ||
      processing.activeOperations > 0
    : true;
  const mine = reading && reading.jobId === jobId ? reading : null;
  const types = mine?.types ?? [];
  // PAIGE: the count only goes up. The job's batch is read a second time behind the window, and the
  // list it reports empties and fills again; the window keeps the most it has shown (`tourReadPeak`).
  const peak = useRef<{ jobId: string; types: (string | null)[] }>({ jobId: '', types: [] });
  peak.current = tourReadPeak(peak.current, jobId, types);
  const seen = peak.current.types;
  const read = seen.filter(Boolean).length;
  const behind = types.filter(Boolean).length < read;
  const settled = Boolean(
    mine && !mine.reading && !busy && types.length > 0 && types.every(Boolean),
  );
  // PAIGE: named apart from `seen` above (the most this window has shown).
  // What this window saw while the five were read, for telemetry: the most read again at once, and a field reader
  // that couldn't read (sent as it shows; a tour left here then says why).
  const sawReading = useRef<{ jobId: string; again: number; problem: ReaderProblem | null }>({
    jobId: '',
    again: 0,
    problem: null,
  });
  useEffect(() => {
    if (!jobId || !mine) return;
    const before =
      sawReading.current.jobId === jobId ? sawReading.current : { jobId, again: 0, problem: null };
    sawReading.current = {
      jobId,
      again: Math.max(before.again, mine.again),
      problem: mine.problem?.cause ?? before.problem,
    };
    if (mine.problem) tourTelemetry.readerProblem(mine.problem.cause, jobId);
  }, [jobId, mine?.again, mine?.problem?.cause]); // eslint-disable-line react-hooks/exhaustive-deps
  // PAIGE: what was read is kept with the moment it finished, so the ready line never counts a list
  // that has since gone (the workspace remounts on the job's own route: "Paige read all no").
  const [done, setDone] = useState<{
    jobId: string;
    seconds: number | null;
    types: (string | null)[];
  } | null>(null);
  const settledTypes = useRef(types);
  if (settled) settledTypes.current = types;
  useEffect(() => {
    if (!settled || !jobId || done?.jobId === jobId) return;
    const timer = window.setTimeout(() => {
      const elapsed = clickedAt ? Date.now() - clickedAt : null;
      setDone({
        jobId,
        seconds: elapsed === null ? null : Math.round(elapsed / 1000),
        types: settledTypes.current,
      });
      const saw =
        sawReading.current.jobId === jobId ? sawReading.current : { again: 0, problem: null };
      tourTelemetry.ready({
        jobId,
        ...(elapsed === null ? {} : { durationMs: elapsed }),
        readAgain: saw.again,
        readerProblem: saw.problem,
      });
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [settled, jobId, done?.jobId]);
  const ready = Boolean(jobId && done?.jobId === jobId);
  // Ready is shown for a moment, then the tour's first card takes over.
  const tourRef = useRef(tour);
  tourRef.current = tour;
  useEffect(() => {
    if (!ready) return;
    const timer = window.setTimeout(() => {
      const now = tourRef.current;
      if (now && !now.ready && now.jobId === jobId) writeTour({ ...now, ready: true });
    }, READY_SHOWN);
    return () => window.clearTimeout(timer);
  }, [ready, jobId]);
  if (!starting && !waiting) return null;

  const total = Math.max(TOUR_DOCUMENTS.length, seen.length);
  const readDone = done?.jobId === jobId ? done.types : [];
  const shown = ready
    ? readDone.map((type) => ({ state: 'read' as const, label: type }))
    : Array.from({ length: total }, (_, at) => {
        const readTypes = seen.filter((type): type is string => Boolean(type));
        if (at < readTypes.length) return { state: 'read' as const, label: readTypes[at] };
        // What is being read now: the documents already found and not yet read, or the next one.
        const beingRead = Math.max(1, seen.length - readTypes.length);
        return {
          state: at < readTypes.length + beingRead ? ('reading' as const) : ('waiting' as const),
          label: null,
        };
      });
  // The engine's own step first, in words; then what is true without one. A first reading that didn't
  // finish is read again by the engine on its own (within a minute or so), and the tour waits for it.
  // Documents only, never pages or files (Brandy, 2026-10-05: "Reading page 0 of 6" beside "0 of 5
  // read" asked "is it five? six?").
  const again = mine?.again ?? 0;
  // The field reader itself can't read: said plainly, with no promise to read them again (not-read.ts).
  const problem = mine?.problem ?? null;
  const step = !jobId
    ? 'Getting the five samples ready'
    : problem
      ? readerProblemStep(problem.cause)
      : ((behind ? 'Finishing up' : tourReadingStep(mine?.stage)) ??
        (again > 0
          ? `Reading ${again === 1 ? '1 document' : `${again} documents`} again`
          : read > 0 && read === seen.length
            ? 'Finishing up'
            : read > 0
              ? 'Reading the rest'
              : tourReadingStep('preparing')));
  const count = tourReadCount(read, total);
  const leave = () => window.dispatchEvent(new Event(TOUR_LEAVE_EVENT));
  // PAIGE: a reading that failed is said, never waited on (paige/TourStartFailed.tsx); samples that
  // never reached Cortexa are said too, with Try again (paige/tour-feed.ts).
  if (jobId && !ready && feedFailed)
    return <TourStartFailed onLeave={leave} onRetry={() => retryTourFeed(jobId)} />;
  if (jobId && !ready && readingFailed) return <TourStartFailed onLeave={leave} />;
  // PAIGE: the desktop reads on the operator's computer; Paige reads in the cloud (`local-storage`).
  const here = hostHas('local-storage') ? ' on this computer' : '';

  return createPortal(
    <div className="tour-start-backdrop">
      {/* Once ready it closes by itself: telemetry counts that as the window done, not left. */}
      <section
        className="tour-start"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-start-title"
        aria-live="polite"
        data-telemetry-complete={ready ? '' : undefined}
      >
        <header>
          {ready ? (
            <span className="tour-start-mark done" aria-hidden="true">
              <Check size={15} strokeWidth={3.2} />
            </span>
          ) : (
            <span className="tour-start-mark" aria-hidden="true">
              <Play size={13} />
            </span>
          )}
          <h2 id="tour-start-title">
            {ready ? 'Your tour is ready' : 'Hang tight, setting up your tour'}
          </h2>
        </header>
        <p>
          {ready
            ? tourReadyLine(readDone.length, here, done?.seconds)
            : `Paige is reading five sample documents${here}, live.`}
        </p>
        <ol className="tour-start-tiles" aria-label={`${count} read`}>
          {shown.map((tile, at) => (
            <Tile key={at} state={tile.state} label={tile.label} />
          ))}
        </ol>
        {!ready && (
          <div className="tour-start-progress">
            <div className="tour-start-bar" aria-hidden="true">
              {shown.map((tile, at) => (
                <i key={at} className={tile.state} />
              ))}
            </div>
            <div className="tour-start-status">
              <span>{step}</span>
              <b>{count}</b>
            </div>
            {problem && <TechnicalDetails detail={problem.detail} />}
          </div>
        )}
        {jobId && !ready && (
          <footer>
            <button
              type="button"
              className="tour-start-leave"
              data-telemetry="tour-leave-reading"
              onClick={leave}
            >
              Leave tour
            </button>
          </footer>
        )}
      </section>
    </div>,
    document.body,
  );
}
